import React from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import { isProductSoldByAProfessionalSeller } from '@helpers/utils/products';
import { scrollTo } from '@helpers/utils/general';
import Icon from '@components/atoms/icon/icon';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import { usePreferences } from '@context/preferences.context';
import dynamic from 'next/dynamic';
import useProductQuery from '@hooks/product/use-product-query';
import { useIntersectionObserver } from 'usehooks-ts';
import ProductDescriptionList from './product-description-list/product-description-list';
import styles from './product-description.module.scss';

const ProductSellerDescription = dynamic(() => import('./product-seller-description/product-seller-description'), {
  ssr: false,
});

const ProductReassurance = dynamic(
  () => import('@components/molecules/products/product-description/product-reassurance/product-reassurance'),
  {
    ssr: false,
  },
);

const ProductSellerBlock = dynamic(() => import('./product-seller-block/product-seller-block'), {
  ssr: false,
});

const LegalWarrantiesModal = dynamic(() => import('./legal-warranties-modal/legal-warranties-modal'), {
  ssr: false,
});

interface ProductDescriptionProps {
  canReportProducts?: boolean;
}

const ProductDescription: React.FC<ProductDescriptionProps> = (props) => {
  const { canReportProducts = false } = props;

  const { t } = useTranslation();
  const { locale } = usePreferences();
  const { product } = useProductQuery();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);

  const isFrench: boolean = locale === 'fr-FR';

  const containerRef = React.useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(containerRef, {
    freezeOnceVisible: true,
    threshold: 0.5,
    rootMargin: '0px 0px 200px 0px',
  });

  const shouldRenderSellerBlock = !!entry?.isIntersecting ?? false;
  const shouldRenderProductReassurance = !!entry?.isIntersecting ?? false;

  const openLegalWarrantiesModal = () =>
    openModal(<LegalWarrantiesModal />, {
      styles: {
        content: {
          maxWidth: '600px',
          width: '100%',
        },
      },
    });

  return (
    <>
      <div
        ref={containerRef}
        className={clsx(styles.description, 'innerContainer')}
      >
        <h2 className={clsx('vc-heading-A24 vc-heading-A40--md-up', styles.description__title)}>
          {t('PRODUCT.DESCRIPTION.TITLE')}
        </h2>
        <div className={styles.description__container}>
          <div className={styles.description__container__seller}>
            {shouldRenderSellerBlock && <ProductSellerBlock product={product} />}
          </div>
          <div className={styles.description__container__core}>
            <ProductSellerDescription product={product} />
            <ProductDescriptionList />
            <Button
              disableDefaultStyling
              onClick={() => scrollTo('#comments')}
              type="submit"
              className={styles.description__discussionsBtn}
              data-cy="product-description-scroll-to-comments-button"
            >
              <Icon
                className={styles.description__discussionsIcon}
                name="talk"
              />
              <span className={styles.description__discussionsText}>
                {t('PRODUCT.DESCRIPTION.SEE_DISCUSSIONS_BTN')}
              </span>
            </Button>
          </div>
          <div className={styles.description__container__reassurance}>
            {shouldRenderProductReassurance && <ProductReassurance />}
            <div className={styles.description__legal}>
              <span>
                {t(
                  isProductSoldByAProfessionalSeller(product)
                    ? 'PRODUCT.DESCRIPTION.REASSURANCE.LEGAL_PRO'
                    : 'PRODUCT.DESCRIPTION.REASSURANCE.LEGAL_PRIVATE',
                )}
              </span>
            </div>
            {isFrench && (
              <Button
                type="submit"
                disableDefaultStyling
                className={clsx(styles.description__legalWarrantiesLink, 'vc-link')}
                onClick={openLegalWarrantiesModal}
                data-cy="product-description-open-legal-warranties-modal-button"
              >
                Garanties légales
              </Button>
            )}
            {canReportProducts && (
              <Button
                disableDefaultStyling
                type="submit"
                className={clsx(styles.description__reportLink, 'vc-link')}
                data-cy="product-description-report-product-button"
              >
                {t('PRODUCT.DESCRIPTION.REASSURANCE.REPORT')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDescription;
